import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthLogin, EyeCloseIcon, EyeIcon } from "asset/icons";
import Input from "component/form/Input";
import ErrorMessage from "../ErrorMessage";
import { COMPLETE_LOGIN, REQUEST_LOGIN } from "store/action";
import { checkAutoLogin } from "store/authServices";
import fetchUserLocation from "utilities/fetchUserLocation";
import hasLoggedInWithinLastMonth from "utilities/hasLoggedInWithinLastMonth";
import Loader from "component/loader";
import Swal from "sweetalert2";

const SignIn = () => {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        email: "",
        password: "",
        keepSignedIn: false,
    });

    const [deviceType, setDeviceType] = useState("");
    const [location, setLocation] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [sendAlert, setSendAlert] = useState(false);
    const navigate = useNavigate();
    const routeLocation = useLocation();

    useEffect(() => {
        setDeviceType(navigator.userAgent);
        fetchUserLocation().then((loc) => setLocation(loc));
        setSendAlert(!hasLoggedInWithinLastMonth());
    }, []);

    useEffect(() => {
        const query = new URLSearchParams(routeLocation.search);
        if (query.get("timeout")) {
            Swal.fire({
                text: "You were logged out due to inactivity.",
                icon: "info",
                confirmButtonText: "Okay",
            });
        } else if (query.get("sessionExpired")) {
            Swal.fire({
                text: "Your session expired. Please sign in again.",
                icon: "info",
                confirmButtonText: "Okay",
            });
        }
    }, [routeLocation.search]);

    const handleChange = (value, name) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);
        const payload = {
            email: formData.email,
            password: formData.password,
            sendAlert: sendAlert.toString(),
            device: deviceType,
            location,
        };

        try {
            const res = await REQUEST_LOGIN(payload);
            sessionStorage.setItem("lastLoginTimestamp", new Date().getTime());

            const loginData = res?.data?.data;
            const user = loginData?.user;
            const mfaRequired = !!loginData?.mfaRequired;

            if (mfaRequired) {
                sessionStorage.setItem("pendingLogin", JSON.stringify(loginData));
                return navigate("/auth/confirm-2fa", { replace: true });
            }

            COMPLETE_LOGIN(loginData, dispatch);
            await checkAutoLogin(dispatch);

            // // Handle account verification logic
            // if (!user?.verification?.smsVerification?.isVerified) {
            //     return window.location.replace(
            //         `/verify-number?phoneNumber=${encodeURIComponent(
            //             user.phoneNumber || user.business?.registrationNumber
            //         )}`
            //     );
            // }

            if (user?.pinStatus === "NOT_VERIFIED") {
                return navigate("/auth/setup-pin", { replace: true });
            }

            const lastVisitedPath = sessionStorage.getItem("lastVisitedPath");
            if (lastVisitedPath && lastVisitedPath !== "/" && lastVisitedPath !== "/auth/signin") {
                sessionStorage.removeItem("lastVisitedPath");
                return navigate(lastVisitedPath, { replace: true });
            } else {
                navigate("/dashboard", { replace: true });
            }
        } catch (error) {
            setErrorMessage(error?.response?.data?.message || "Login failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-[500px] xl:max-w-[650px] !py-4 mt-10 lg:!mt-0 md:mt-0 !px-3 bg-white rounded-2xl border border-[#EBEDF9]">
            <AuthLogin className="h-24 w-24" />

            <div className="md:px-6 pb-8">
                <div className="mb-6">
                    <h2 className="text-3xl font-semibold text-[#101828]">Welcome back</h2>
                    <p className="text-[#667085] text-sm">
                        Sign in to your dashboard or{" "}
                        <span
                            onClick={() => {
                                navigate("/auth/signup");
                            }}
                            className="text-[#6f00ff] font-medium cursor-pointer"
                        >
                            Create an account
                        </span>
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 w-full">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-[#344054] mb-2">
                            Email
                        </label>
                        <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(value) => handleChange(value, "email")}
                            placeholder="Enter your email"
                        />
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-medium text-[#344054] mb-2">
                            Password
                        </label>
                        <div className="relative">
                            <Input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={(value) => handleChange(value, "password")}
                                placeholder="Enter your password"
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-3 flex items-center text-[#667085]"
                                onClick={() => setShowPassword((prev) => !prev)}
                            >
                                {showPassword ? (
                                    <EyeCloseIcon className="w-6 h-6" />
                                ) : (
                                    <EyeIcon className="w-7 h-7" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* error message */}
                    {errorMessage && <ErrorMessage message={errorMessage} />}

                    {/* Footer buttons */}
                    <div className="flex justify-end !mt-5 !gap-5 items-center">
                        <span
                            onClick={() => navigate("/auth/recover-password")}
                            className="text-[#6f00ff] text-md font-medium cursor-pointer"
                        >
                            Forgot password?
                        </span>
                        <button
                            type="submit"
                            disabled={!formData.email || !formData.password || loading}
                            className={`px-6 py-3 rounded-lg text-white text-sm font-semibold ${
                                !formData.email || !formData.password || loading
                                    ? "bg-[#E9D7FE] cursor-not-allowed"
                                    : "bg-[#6f00ff] hover:bg-[#6B46C1]"
                            }`}
                        >
                            {loading ? <Loader /> : "Sign in"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SignIn;
